import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
  ReactNode,
} from 'react'
import { KAKERU_MSG, ANYCOMPASS_PARENT_ORIGINS } from '../constants/embed'
import {
  KakeruEnvelope,
  KakeruInitParam,
  KakeruApplySoapParam,
} from '../types/embed'

// AnyCOMPASS iframe 埋め込みの「コンテキスト検出」本体(ハンドシェイク)。
//
// 単なる self!==top ではなく、双方向・レース安全・オリジン検証済みのハンドシェイクで
// 「誰に埋め込まれているか」を確定する。信用源は検証済みメッセージの event.origin のみ。
// referrer / ancestorOrigins / self!==top は候補の絞り込み(入口ヒント)に留め、
// 書き戻し(APPLY_SOAP)の送信先は INIT を受けて pin した origin に固定する。
//
// 設計正本: medigital-hub log/04 work-log/01_iframe_wiring_protocol.md §2。

interface AnyCompassEmbedContextValue {
  // 検証済みハンドシェイクが成立し、信頼できる親 origin に pin 済みか。
  // 転記ボタンの点灯条件(§3)の 1 つ。
  isEmbedded: boolean
  // pin した親 origin(APPLY_SOAP の送信先)。未成立なら null。
  parentOrigin: string | null
  // INIT で親から受けた患者コンテキスト(突合ガードの真値源)。
  patientContext: KakeruInitParam | null
  // 起動 URL の ?patient_name= (親が deeplink で指定した患者)。単体起動では null。
  // 埋め込みで患者コンテキストを失った画面 (landing) が親の患者へ戻すために使う。
  deeplinkPatientName: string | null
  // 生成 SOAP を親へ転記送出する。pin 未成立なら false を返して何もしない。
  applySoap: (param: KakeruApplySoapParam) => boolean
}

const AnyCompassEmbedContext = createContext<
  AnyCompassEmbedContextValue | undefined
>(undefined)

// READY 再送(親が listener 登録前に READY を取りこぼすレースの保険)。
const READY_RETRY_INTERVAL_MS = 400
const READY_MAX_TRIES = 15

// referrer / ancestorOrigins から、許可リストに載る候補親 origin を推定する。
// ヒントが取れない/一致しなければ許可リスト全体へ fan-out(各 origin が検証するので無害)。
const resolveCandidateOrigins = (): readonly string[] => {
  const allow = ANYCOMPASS_PARENT_ORIGINS
  if (allow.length === 0) return []

  const hints = new Set<string>()
  // ancestorOrigins: Chrome/Safari のみ。[0] が直上の親 origin。
  const ancestors = window.location.ancestorOrigins
  if (ancestors && ancestors.length > 0) hints.add(ancestors[0])
  // referrer: 親ページの URL。origin だけ取り出す。
  if (document.referrer) {
    try {
      hints.add(new URL(document.referrer).origin)
    } catch {
      // 不正な referrer は無視
    }
  }

  const matched = allow.filter((o) => hints.has(o))
  return matched.length > 0 ? matched : allow
}

// 起動 URL のクエリを 1 つ読む。trim して空なら null (「載っていない」と「空で載っている」を
// 区別しない)。Provider の初回 render で 1 回だけ呼ぶこと: iframe の初回 URL は
// /yakureki/quick-record?… で、quick-record が navigate(replace) でクエリを消すため、
// このタイミングを逃すと二度と読めない。
const readDeeplinkParam = (name: string): string | null => {
  const raw = new URLSearchParams(window.location.search).get(name)
  const trimmed = raw?.trim() ?? ''
  return trimmed === '' ? null : trimmed
}

export const AnyCompassEmbedProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [parentOrigin, setParentOrigin] = useState<string | null>(null)
  const [patientContext, setPatientContext] = useState<KakeruInitParam | null>(
    null
  )
  // message listener / interval 内から最新の pin を参照するための鏡(再登録回避)。
  const pinnedOriginRef = useRef<string | null>(null)

  // 親が deeplink で渡した患者 (?patient_name=)。
  // iframe の初回 URL は必ず /yakureki/quick-record?patient_name=… で、quick-record が
  // navigate(replace) でクエリを消すため、Provider の初回 render で確保しないと二度と
  // 取れない。Provider は RouterProvider の外側にあるので、以後どこへ遷移しても生き残る。
  //
  // INIT の knjName (patientContext) と混同しないこと。あちらは READY 往復の後に届き、
  // 親が送らなければ永久に来ない。認証退避などで患者コンテキストを失った画面が
  // 親の患者へ戻るときはこちら (URL 由来) を使う (log/193)。
  const [deeplinkPatientName] = useState<string | null>(() =>
    readDeeplinkParam('patient_name')
  )

  useEffect(() => {
    // トップレベル(iframe でない)なら埋め込みではない。許可リスト空も同様に無効。
    if (window.self === window.top || ANYCOMPASS_PARENT_ORIGINS.length === 0) {
      return
    }

    const candidates = resolveCandidateOrigins()
    if (candidates.length === 0) return

    let readyTimer: ReturnType<typeof setInterval> | undefined

    const stopReady = () => {
      if (readyTimer !== undefined) {
        clearInterval(readyTimer)
        readyTimer = undefined
      }
    }

    const handleMessage = (event: MessageEvent) => {
      // 直上の親からのメッセージのみ受理(兄弟 frame 等の spoof を弾く)。
      if (event.source !== window.parent) return
      // 信用源は検証済み origin のみ。許可リスト外は破棄。
      if (!ANYCOMPASS_PARENT_ORIGINS.includes(event.origin)) return

      const data = event.data as KakeruEnvelope<KakeruInitParam> | undefined
      if (!data || typeof data !== 'object') return

      if (data.actionName === KAKERU_MSG.INIT) {
        // 親 origin を pin(以降 APPLY_SOAP の送信先はここに固定)。
        pinnedOriginRef.current = event.origin
        setParentOrigin(event.origin)
        setPatientContext(data.param ?? null)
        stopReady()
      }
    }

    window.addEventListener('message', handleMessage)

    // READY 先出し + 再送。pin 成立 or 最大試行で停止。
    const readyEnvelope: KakeruEnvelope<Record<string, never>> = {
      actionName: KAKERU_MSG.READY,
      keyNo: '',
      param: {},
    }
    let tries = 0
    const sendReady = () => {
      if (pinnedOriginRef.current !== null || tries >= READY_MAX_TRIES) {
        stopReady()
        return
      }
      tries += 1
      // 候補各 origin へ送出。targetOrigin を厳格指定するので、実親と一致しない分は
      // ブラウザが黙って破棄する(=無害)。'*' は使わない。
      candidates.forEach((origin) => {
        window.parent.postMessage(readyEnvelope, origin)
      })
    }
    sendReady()
    readyTimer = setInterval(sendReady, READY_RETRY_INTERVAL_MS)

    return () => {
      window.removeEventListener('message', handleMessage)
      stopReady()
    }
  }, [])

  const applySoap = useCallback((param: KakeruApplySoapParam): boolean => {
    const origin = pinnedOriginRef.current
    if (origin === null) {
      // pin 未成立(embed 未確立)で呼ばれた場合は送信先が無いので送らない。
      console.warn('[anycompass-embed] applySoap called before handshake pin')
      return false
    }
    const envelope: KakeruEnvelope<KakeruApplySoapParam> = {
      actionName: KAKERU_MSG.APPLY_SOAP,
      keyNo: param.patientKey,
      param,
    }
    window.parent.postMessage(envelope, origin)
    return true
  }, [])

  const value = useMemo<AnyCompassEmbedContextValue>(
    () => ({
      isEmbedded: parentOrigin !== null,
      parentOrigin,
      patientContext,
      deeplinkPatientName,
      applySoap,
    }),
    [parentOrigin, patientContext, deeplinkPatientName, applySoap]
  )

  return (
    <AnyCompassEmbedContext.Provider value={value}>
      {children}
    </AnyCompassEmbedContext.Provider>
  )
}

export const useAnyCompassEmbed = (): AnyCompassEmbedContextValue => {
  const ctx = useContext(AnyCompassEmbedContext)
  if (!ctx) {
    throw new Error(
      'useAnyCompassEmbed must be used within AnyCompassEmbedProvider'
    )
  }
  return ctx
}
